import { Maximize2 } from "lucide-react";
import { ResponsiveContainer } from "recharts";
import { ChartContainerProps } from "./types";
import { Loader } from "@/components/ui/Loader";
import { cn } from "@/utils/cn";

export function ChartContainer({
  title,
  subtitle,
  titleClassName,
  subtitleClassName,
  children,
  loading   = false,
  empty     = false,
  emptyMessage = "Sem dados para exibir.",
  className,
  onExpand,
  headerAction,
  height    = 300,
  fillSpace = false,
  contentStretch = false,
}: ChartContainerProps) {
  const fillChart = fillSpace && typeof height === "number";
  const stretchAutoBody = height === "auto" && contentStretch;

  return (
    <div
      className={cn(
        "bg-white rounded-xl border border-slate-200 shadow-card p-5 flex flex-col gap-4",
        fillChart && "h-full min-h-0",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className={cn("text-sm font-semibold text-slate-800 leading-tight", titleClassName)}>{title}</h3>
          {subtitle && (
            <p className={cn("text-xs text-slate-500 mt-0.5", subtitleClassName)}>{subtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {headerAction}
          {onExpand && (
            <button
              onClick={onExpand}
              className="p-1.5 rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              title="Expandir gráfico"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* minWidth:0 evita colapso em flex/grid */}
      <div
        className={cn(
          "min-w-0",
          fillChart || stretchAutoBody ? "flex min-h-0 flex-1 flex-col" : "w-full",
        )}
        style={
          fillChart
            ? { minHeight: height, overflow: "hidden" }
            : height !== "auto"
              ? { height, minWidth: 0, overflow: "hidden" }
              : { minWidth: 0 }
        }
      >
        {loading ? (
          <div
            className={cn(
              fillChart || stretchAutoBody ? "flex min-h-0 flex-1 items-center justify-center" : height !== "auto" ? "flex h-full items-center justify-center" : "flex items-center justify-center py-10",
            )}
          >
            <Loader size="md" label="Carregando dados..." />
          </div>
        ) : empty ? (
          <div
            className={cn(
              fillChart || stretchAutoBody ? "flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-slate-400" : height !== "auto" ? "flex h-full flex-col items-center justify-center gap-2 text-slate-400" : "flex flex-col items-center justify-center gap-2 py-10 text-slate-400",
            )}
          >
            <svg className="w-10 h-10 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <p className="text-sm font-medium">{emptyMessage}</p>
          </div>
        ) : height === "auto" ? (
          /* Conteúdo de altura livre — sem ResponsiveContainer (ex.: tabelas, listas) */
          <>{children}</>
        ) : fillChart ? (
          <div className="flex min-h-0 w-full flex-1 flex-col">
            <ResponsiveContainer width="100%" height="100%">
              {children as React.ReactElement}
            </ResponsiveContainer>
          </div>
        ) : (
          /* Gráfico Recharts — height numérico evita que ResponsiveContainer meça -1 */
          <ResponsiveContainer width="100%" height={height}>
            {children as React.ReactElement}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
